import React, { FC, useCallback, useMemo, useState } from 'react';
import styled from 'styled-components';
import { ApolloError } from '@apollo/client';
import { allMatch } from 'utils/predicateUtils';
import { JobFamilyGroupCreateModelInput, JobFamilyGroupModel } from '@utmost.co/enterprise-models';
import { Dialog, Input, Button } from '@beeline.com/bp-design-system';
import { useSnackbar } from 'src/utils/hooks/useSnackbar';
import { useCreateJobFamilyGroup } from './useCreateJobFamilyGroup';
import { usePatchJobFamilyGroup } from './usePatchJobFamilyGroup';
import { useJobFamilyGroupsIntl } from './translations/useJobFamilyGroupsIntl';

interface Props {
  jobFamilyGroup?: JobFamilyGroupModel | null;
  onClose: () => void;
  onUpdated: () => void;
}

export const JobFamilyGroupDialog: FC<React.PropsWithChildren<Props>> = ({ onClose, jobFamilyGroup, onUpdated }) => {
  const intl = useJobFamilyGroupsIntl();
  const [name, setName] = useState(jobFamilyGroup?.name ?? '');
  const [referenceId, setReferenceId] = useState(jobFamilyGroup?.referenceId ?? '');
  const isAdd = !jobFamilyGroup;
  const { showSuccess, showError } = useSnackbar();

  const handleApolloError = useCallback((error: ApolloError) => showError(error.message), [showError]);

  const handleApolloSuccess = useCallback(() => {
    onUpdated();
    showSuccess(isAdd ? intl.jobFamilyGroupSuccessfullyAdded : intl.jobFamilyGroupSuccessfullyUpdated);
  }, [intl.jobFamilyGroupSuccessfullyAdded, intl.jobFamilyGroupSuccessfullyUpdated, isAdd, onUpdated, showSuccess]);

  const createJobFamilyGroup = useCreateJobFamilyGroup(handleApolloSuccess, handleApolloError);
  const patchJobFamilyGroup = usePatchJobFamilyGroup(handleApolloSuccess, handleApolloError);

  const canAdd = useMemo(
    () => !createJobFamilyGroup.loading && allMatch([name, referenceId], Boolean),
    [createJobFamilyGroup.loading, name, referenceId]
  );

  const handleAdd = useCallback(() => {
    if (createJobFamilyGroup.loading) {
      return;
    }
    const newJobFamilyGroup: Partial<JobFamilyGroupCreateModelInput> = {
      name,
      referenceId
    };
    createJobFamilyGroup.create({
      jobFamilyGroup: newJobFamilyGroup as JobFamilyGroupCreateModelInput
    });
  }, [createJobFamilyGroup, name, referenceId]);

  const handleEdit = useCallback(async () => {
    if (jobFamilyGroup?.uid) {
      await patchJobFamilyGroup.patch({
        uid: jobFamilyGroup?.uid,
        name,
        referenceId
      });
      setName('');
      setReferenceId('');
    }
  }, [jobFamilyGroup?.uid, name, patchJobFamilyGroup, referenceId]);

  return (
    <Dialog
      open
      onClose={onClose}
      title={isAdd ? intl.addJobFamilyGroup : intl.editJobFamilyGroup}
      subTitle={isAdd ? intl.enterDetailsToCreateJobFamilyGroup : intl.updateJobFamilyGroupDetails}
      dataTest="add-job-family-group-dialog"
      show={{ cancelButton: true }}
      width="sm"
      actions={[
        <Button
          onClick={isAdd ? handleAdd : handleEdit}
          disabled={!canAdd}
          key="save"
          dataTest="add-job-family-save-button"
          color="primary"
          variant="contained"
        >
          {isAdd ? intl.add : intl.save}
        </Button>
      ]}
    >
      <StyledDialogContainer>
        <StyledFirstLine>
          <Input
            label={intl.groupName}
            id="name"
            name="name"
            dataTest="job-family-group-name"
            autoFocus
            required
            placeholder={`Enter ${intl.groupName}`}
            value={name}
            fullWidth
            onChange={(e) => setName(e.target.value)}
          />
        </StyledFirstLine>
        <StyledLine>
          <Input
            label={intl.referenceId}
            id="referenceId"
            name="referenceId"
            dataTest="job-family-group-reference-id"
            required
            placeholder={`Enter ${intl.referenceId}`}
            value={referenceId}
            fullWidth
            type="email"
            onChange={(e) => setReferenceId(e.target.value)}
          />
        </StyledLine>
      </StyledDialogContainer>
    </Dialog>
  );
};
const StyledDialogContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;
const StyledLine = styled.div`
  margin: 1rem 0;
  min-height: 35px;
`;
const StyledFirstLine = styled(StyledLine)`
  margin-top: 0;
`;
